import React, { useCallback, useEffect, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupsIcon from "@mui/icons-material/Groups";
import SearchIcon from "@mui/icons-material/Search";

import configServ from "../services/config";
import { rSetShowFab } from "../redux/reducers/GlobalVariables";

/* ==================================================================
 * CONSTANTS
 * ================================================================== */

const PER_PAGE = 20;
const SELECT_ALL_PER_PAGE = 100;
const SEARCH_DEBOUNCE_MS = 500;
const SCROLL_THRESHOLD_PX = 40;
const ACTIVE_STATUS = 1;
const MAX_NAMES_SHOWN = 2;

/* ==================================================================
 * HELPERS
 * ================================================================== */

let rowSeq = 0;
const createRow = () => ({
  uid: ++rowSeq, // stable React key
  leave_type_id: "",
  days: "",
  carry_forward: false,
});

const mapEmployee = (employee) => ({
  id: Number(employee.id),
  code: employee.employee_code || employee.emp_code || "",
  name:
    employee.contact_name ||
    `${employee.fname || ""} ${employee.lname || ""}`.trim() ||
    employee.name ||
    "Unnamed",
});

const toEmployeeMap = (employees) =>
  employees.reduce((acc, e) => {
    acc[Number(e.id)] = e;
    return acc;
  }, {});

const isValidDays = (value) =>
  value !== "" && Number.isFinite(Number(value)) && Number(value) >= 0;

/* ==================================================================
 * EMPLOYEE SELECTOR MODAL
 * Mount only while visible: {open && <EmployeeSelectorModal />}
 * ================================================================== */

function EmployeeSelectorModal({
  selectedEmployees,
  onSave,
  onClose,
  companyId,
  userId,
  isAdmin,
}) {
  /* ---------------- State ---------------- */
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [departmentList, setDepartmentList] = useState([]);

  const [employeeList, setEmployeeList] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [modalError, setModalError] = useState("");

  // id -> employee (keeps names of employees not in the loaded page)
  const [selectedMap, setSelectedMap] = useState(() =>
    toEmployeeMap(selectedEmployees)
  );

  const requestRef = useRef(0); // ignores stale responses

  /* ---------------- Derived values ---------------- */
  const selectedCount = Object.keys(selectedMap).length;
  const hasFilters = Boolean(debouncedSearch || departmentId);

  /* ---------------- API / business logic ---------------- */
  const buildPayload = useCallback(
    (pageNum, perPage) => {
      const term = debouncedSearch.trim();
      return {
        company_id: companyId,
        user_id: userId,
        is_admin: isAdmin,
        status: ACTIVE_STATUS,
        per_page: perPage,
        page: pageNum,
        ...(term && { search: term }),
        ...(departmentId && { department_id: departmentId }),
      };
    },
    [companyId, userId, isAdmin, debouncedSearch, departmentId]
  );

  const fetchEmployees = useCallback(
    async (pageNum) => {
      if (!companyId || !userId) return;

      const requestId = ++requestRef.current;
      setLoading(true);
      setModalError("");

      try {
        const response = await configServ.getEmployees(
          buildPayload(pageNum, PER_PAGE)
        );
        if (requestId !== requestRef.current) return;

        if (response?.status !== true) throw new Error(response?.message);

        const { data = [], pagination = {} } = response;
        const mapped = data.map(mapEmployee);

        setEmployeeList((prev) => (pageNum === 1 ? mapped : [...prev, ...mapped]));
        setPage(pageNum);
        setTotal(pagination.total || 0);
        setHasMore(pageNum < (pagination.last_page || 1));
      } catch (error) {
        if (requestId === requestRef.current) setModalError(error.message);
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    },
    [companyId, userId, buildPayload]
  );

  const fetchAllEmployees = async () => {
    const all = [];
    let pageNum = 1;
    let lastPage = 1;

    do {
      const response = await configServ.getEmployees(
        buildPayload(pageNum, SELECT_ALL_PER_PAGE)
      );
      if (response?.status !== true) throw new Error(response?.message);

      all.push(...(response.data || []).map(mapEmployee));
      lastPage = response.pagination?.last_page || 1;
      pageNum += 1;
    } while (pageNum <= lastPage);

    return all;
  };

  /* ---------------- Effects ---------------- */

  // Departments (once)
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await configServ.getAllDepartments(companyId);
        if (!cancelled && res?.status) setDepartmentList(res.data || []);
      } catch (error) {
        console.error(error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Page 1 load: on mount and whenever search / department changes
  useEffect(() => {
    setEmployeeList([]);
    setPage(1);
    setHasMore(true);
    fetchEmployees(1);
  }, [fetchEmployees]);

  /* ---------------- Handlers ---------------- */
  const handleScroll = ({ currentTarget }) => {
    const { scrollTop, clientHeight, scrollHeight } = currentTarget;
    const nearBottom = scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD_PX;
    if (nearBottom && hasMore && !loading) fetchEmployees(page + 1);
  };

  const handleToggleEmployee = (employee) => {
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (next[employee.id]) delete next[employee.id];
      else next[employee.id] = employee;
      return next;
    });
  };

  const handleSelectAll = async () => {
    setSelectingAll(true);
    setModalError("");
    try {
      const all = await fetchAllEmployees();
      setSelectedMap((prev) => ({ ...prev, ...toEmployeeMap(all) }));
    } catch (error) {
      setModalError(error.message);
    } finally {
      setSelectingAll(false);
    }
  };

  const handleClear = () => setSelectedMap({});

  const handleSave = () => {
    onSave(Object.values(selectedMap));
    onClose();
  };

  /* ---------------- UI ---------------- */
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        Select Employees
        <Typography
          variant="caption"
          sx={{ display: "block", color: "text.secondary", fontWeight: 400 }}
        >
          {total} active employees{hasFilters ? " match the filters" : ""}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        <TextField
          select
          fullWidth
          size="small"
          label="Department"
          value={departmentId}
          onChange={(e) => setDepartmentId(e.target.value)}
          sx={{ mb: 1.5 }}
        >
          <MenuItem value="">All Departments</MenuItem>
          {departmentList.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              {d.department_name || d.name || d.title}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          size="small"
          placeholder="Search by name or code"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />,
          }}
          sx={{ mb: 1.5 }}
        />

        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleSelectAll}
            disabled={selectingAll || loading || total === 0}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {selectingAll ? (
              <>
                <CircularProgress size={14} sx={{ mr: 1 }} /> Selecting...
              </>
            ) : (
              `Select all ${total}`
            )}
          </Button>
          <Button
            size="small"
            color="error"
            onClick={handleClear}
            disabled={selectedCount === 0 || selectingAll}
            sx={{ textTransform: "none" }}
          >
            Clear
          </Button>
        </Box>

        {modalError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {modalError}
          </Alert>
        )}

        <List dense sx={{ maxHeight: 360, overflowY: "auto" }} onScroll={handleScroll}>
          {employeeList.map((employee) => (
            <ListItem key={employee.id} disablePadding>
              <ListItemButton onClick={() => handleToggleEmployee(employee)}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={Boolean(selectedMap[employee.id])}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary={employee.name} secondary={employee.code} />
              </ListItemButton>
            </ListItem>
          ))}

          {loading && (
            <ListItem>
              <CircularProgress size={24} sx={{ mx: "auto" }} />
            </ListItem>
          )}

          {!loading && employeeList.length === 0 && !modalError && (
            <ListItem>
              <Typography variant="body2" color="text.secondary">
                No employees found.
              </Typography>
            </ListItem>
          )}
        </List>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <Button variant="outlined" color="error" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={selectingAll}>
          OK ({selectedCount} selected)
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ==================================================================
 * MAIN PAGE
 * ================================================================== */

export default function BulkLeaveBalance() {
  /* ---------------- Redux ---------------- */
  const dispatch = useDispatch();
  const { company_id, user_id, is_super, is_admin } = useSelector(
    (state) => state.GlobalVariables || state.globalVariables || {},
    shallowEqual
  );

  /* ---------------- State ---------------- */
  // Dropdown data
  const [sessions, setSessions] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);

  // Form data
  const [sessionId, setSessionId] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [rows, setRows] = useState(() => [createRow()]);

  // UI state
  const [errors, setErrors] = useState({});
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);

  /* ---------------- Derived values ---------------- */
  const canAddRow = !loadingOptions && rows.length < leaveTypes.length;

  const employeeFieldText =
    selectedEmployees.length <= MAX_NAMES_SHOWN
      ? selectedEmployees.map((e) => e.code || e.name).join(", ")
      : `${selectedEmployees.length} selected`;

  /* ---------------- API / business logic ---------------- */
  const fetchOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const [sessionsRes, typesRes] = await Promise.all([
        configServ.getSessions({ company_id }),
        configServ.getleaveTypes(),
      ]);

      if (sessionsRes?.status) {
        setSessions(sessionsRes.data);
        const active = sessionsRes.data.find((s) => s.is_active);
        if (active) setSessionId(active.id);
      }
      if (typesRes?.status) setLeaveTypes(typesRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingOptions(false);
    }
  }, [company_id]);

  const validate = () => {
    const newErrors = {};

    if (!sessionId) newErrors.sessionId = "Session is required";
    if (selectedEmployees.length === 0) {
      newErrors.employees = "Select at least one employee";
    }

    const rowErrors = rows.map((row) => {
      const rowError = {};
      if (!row.leave_type_id) rowError.leave_type_id = "Select a leave type";
      if (!isValidDays(row.days)) rowError.days = "Enter valid days";
      return rowError;
    });
    if (rowErrors.some((e) => Object.keys(e).length > 0)) newErrors.rows = rowErrors;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => ({
    company_id,
    session_id: sessionId,
    employee_ids: selectedEmployees.map((e) => Number(e.id)),
    allocations: rows.map((row) => ({
      leave_type_id: row.leave_type_id,
      allotted_leaves: Number(row.days),
      carry_forward_enabled: row.carry_forward ? 1 : 0,
    })),
  });

  // Leave types not already picked in another row (plus this row's own pick)
  const getAvailableLeaveTypes = (currentRow) => {
    const usedElsewhere = rows
      .filter((row) => row.uid !== currentRow.uid)
      .map((row) => row.leave_type_id)
      .filter(Boolean);

    return leaveTypes.filter(
      (type) => !usedElsewhere.includes(type.id) || type.id === currentRow.leave_type_id
    );
  };

  /* ---------------- Effects ---------------- */

  // Hide the floating action button on this page
  useEffect(() => {
    dispatch(rSetShowFab(false));
    return () => {
      dispatch(rSetShowFab(true));
    };
  }, [dispatch]);

  // Load sessions and leave types
  useEffect(() => {
    if (company_id) fetchOptions();
  }, [company_id, fetchOptions]);

  /* ---------------- Handlers ---------------- */
  const handleRowChange = (uid, field, value) => {
    setRows((prev) =>
      prev.map((row) => (row.uid === uid ? { ...row, [field]: value } : row))
    );
  };

  const handleAddRow = () => setRows((prev) => [...prev, createRow()]);

  const handleRemoveRow = (uid) =>
    setRows((prev) => prev.filter((row) => row.uid !== uid));

  const handleSubmit = async () => {
    if (submitting || !validate()) return;

    setSubmitting(true);
    try {
      const response = await configServ.createLeaveBalance(buildPayload());

      if (response?.status) {
        setRows([createRow()]);
        setSelectedEmployees([]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmployeeFieldKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setEmployeeModalOpen(true);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <Box sx={{ p: 2, width: "100%", minHeight: "100vh", bgcolor: "#ffffff" }}>
      <Typography
        variant="h5"
        align="center"
        fontWeight="bold"
        color="primary"
        sx={{ mb: 3, mt: 2 }}
      >
        BULK LEAVE BALANCE
      </Typography>

      <Card elevation={3} sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The leave days you set here will be assigned to all selected employees at
            once.
          </Typography>

          <Grid container spacing={2}>
            {/* Session */}
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Session"
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
                error={Boolean(errors.sessionId)}
                helperText={errors.sessionId}
                disabled={loadingOptions}
              >
                {sessions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.session} ({s.start_date} – {s.end_date})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Employees (opens the selector dialog) */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Employees"
                placeholder="Select employees"
                value={employeeFieldText}
                error={Boolean(errors.employees)}
                helperText={errors.employees}
                onClick={() => setEmployeeModalOpen(true)}
                onKeyDown={handleEmployeeFieldKeyDown}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <GroupsIcon
                      sx={{ mr: 1, color: "text.disabled" }}
                      fontSize="small"
                    />
                  ),
                }}
                inputProps={{
                  "aria-haspopup": "dialog",
                  style: { cursor: "pointer" },
                }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Days per Leave Type
          </Typography>

          {rows.map((row, index) => {
            const rowError = errors.rows?.[index] || {};
            return (
              <Grid
                container
                spacing={2}
                alignItems="center"
                key={row.uid}
                sx={{ mb: 2 }}
              >
                <Grid item xs={12} sm={5}>
                  <TextField
                    select
                    fullWidth
                    label="Leave Type"
                    value={row.leave_type_id}
                    onChange={(e) =>
                      handleRowChange(row.uid, "leave_type_id", e.target.value)
                    }
                    error={Boolean(rowError.leave_type_id)}
                    helperText={rowError.leave_type_id}
                    disabled={loadingOptions}
                  >
                    {getAvailableLeaveTypes(row).map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.leave_name} ({type.leave_code})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={7} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Days"
                    value={row.days}
                    onChange={(e) => handleRowChange(row.uid, "days", e.target.value)}
                    error={Boolean(rowError.days)}
                    helperText={rowError.days}
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>

                <Grid item xs={5} sm={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={row.carry_forward}
                        onChange={(e) =>
                          handleRowChange(row.uid, "carry_forward", e.target.checked)
                        }
                      />
                    }
                    label="Carry Forward"
                  />
                </Grid>

                <Grid item xs={12} sm={1}>
                  <IconButton
                    color="error"
                    aria-label="Remove leave type"
                    onClick={() => handleRemoveRow(row.uid)}
                    disabled={rows.length === 1}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Grid>
              </Grid>
            );
          })}

          <Button
            startIcon={<AddIcon />}
            onClick={handleAddRow}
            disabled={!canAddRow}
            sx={{ textTransform: "none", fontWeight: 600, mt: 1 }}
          >
            Add Leave Type
          </Button>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || loadingOptions}
              sx={{ borderRadius: 2, px: 4, minWidth: 220, height: 45 }}
            >
              {submitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                `Assign to ${selectedEmployees.length} employees`
              )}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {employeeModalOpen && (
        <EmployeeSelectorModal
          selectedEmployees={selectedEmployees}
          onSave={setSelectedEmployees}
          onClose={() => setEmployeeModalOpen(false)}
          companyId={company_id}
          userId={user_id}
          isAdmin={Boolean(is_super || is_admin)}
        />
      )}
    </Box>
  );
}